// Chat WebSocket Service
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

class ChatWebSocket {
  constructor() {
    this.ws = null;
    this.channel = null;
    this.user = null;
    this.listeners = {};
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.reconnectDelay = 1000;
    this.typingTimeout = null;
    this.pollingInterval = null;
    this.usePolling = false;
    this.lastMessageTimestamp = null;
  }

  connect(channel, user) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.disconnect();
    }

    this.channel = channel;
    this.user = user;

    // Build WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = API_URL.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const params = new URLSearchParams({
      user_id: user.id || '',
      user_name: user.name || 'Anonymous',
      user_email: user.email || '',
      profile_picture: user.profile_picture || ''
    });
    
    const wsUrl = `${wsProtocol}//${wsHost}/api/chat/ws/${channel}?${params}`;
    
    try {
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected to', channel);
        this.reconnectAttempts = 0;
        this.emit('connected', { channel });
        // Request user list
        this.send({ type: 'get_users' });
        // Start ping interval to keep connection alive
        this.startPingInterval();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Ignore pong messages (just keep-alive responses)
          if (data.type === 'pong') return;
          this.handleMessage(data);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.stopPingInterval();
        this.emit('disconnected', { channel });
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.emit('error', { error });
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.attemptReconnect();
    }
  }

  startPingInterval() {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
      }
    }, 30000); // Ping every 30 seconds
  }

  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.channel && this.user) {
      this.reconnectAttempts++;
      console.log(`Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
      setTimeout(() => {
        this.connect(this.channel, this.user);
      }, this.reconnectDelay * this.reconnectAttempts);
    } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // Fall back to polling mode
      console.log('WebSocket failed, falling back to polling mode');
      this.usePolling = true;
      this.startPolling();
      // Fetch initial messages immediately
      this.fetchMessages();
      // Emit connected event even in polling mode so UI works
      this.emit('connected', { channel: this.channel, polling: true });
      this.emit('userList', { users: [this.user], count: 1 });
    }
  }

  async fetchMessages() {
    try {
      const response = await fetch(`${API_URL}/api/community/messages/${encodeURIComponent(this.channel)}`);
      if (response.ok) {
        const messages = await response.json();
        if (messages.length > 0) {
          this.lastMessageTimestamp = messages[messages.length - 1].timestamp;
          // Emit all messages as initial load
          this.emit('initialMessages', messages);
        }
      }
    } catch (e) {
      console.error('Failed to fetch messages:', e);
    }
  }

  startPolling() {
    if (this.pollingInterval) clearInterval(this.pollingInterval);
    
    this.pollingInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/community/messages/${this.channel}`);
        if (response.ok) {
          const messages = await response.json();
          // Check for new messages
          if (messages.length > 0) {
            const latestMsg = messages[messages.length - 1];
            if (this.lastMessageTimestamp !== latestMsg.timestamp) {
              // New message received
              if (this.lastMessageTimestamp) {
                const newMessages = messages.filter(m => m.timestamp > this.lastMessageTimestamp);
                newMessages.forEach(msg => this.emit('message', msg));
              }
              this.lastMessageTimestamp = latestMsg.timestamp;
            }
          }
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }, 10000); // Poll every 10 seconds (reduced from 3s for performance)
  }

  disconnect() {
    this.stopPingInterval();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.channel = null;
    this.usePolling = false;
    this.reconnectAttempts = 0;
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
      return true;
    }
    console.warn('WebSocket not ready, message not sent:', data.type);
    return false;
  }

  async sendMessage(text, options = {}) {
    // Don't send empty messages
    if (!text || !text.trim()) {
      if (!options.imageUrl) return; // Allow image-only messages
    }
    
    if (this.usePolling) {
      // In polling mode, send via REST API
      try {
        const response = await fetch(`${API_URL}/api/community/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            channel: this.channel,
            text: text,
            image_url: options.imageUrl
          })
        });
        if (response.ok) {
          const message = await response.json();
          this.emit('message', message);
          // Update timestamp to avoid duplicate on next poll
          this.lastMessageTimestamp = message.timestamp;
        }
      } catch (e) {
        console.error('Failed to send message:', e);
      }
    } else {
      const sent = this.send({
        type: 'message',
        text: text.trim(),
        reply_to: options.replyTo?.id,
        reply_preview: options.replyTo ? {
          author: options.replyTo.author_name,
          text: options.replyTo.text?.substring(0, 100)
        } : null,
        mentions: options.mentions || [],
        image_url: options.imageUrl
      });
      
      if (!sent) {
        console.error('Failed to send message via WebSocket, trying REST API');
        // Fallback to REST API if WebSocket fails
        try {
          const response = await fetch(`${API_URL}/api/community/messages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              channel: this.channel,
              text: text,
              image_url: options.imageUrl
            })
          });
          if (response.ok) {
            const message = await response.json();
            this.emit('message', message);
          }
        } catch (e) {
          console.error('REST fallback also failed:', e);
        }
        // Try to reconnect
        this.attemptReconnect();
      }
    }
    // Stop typing indicator when message is sent
    this.stopTyping();
  }

  startTyping() {
    if (this.usePolling) return; // Skip typing in polling mode
    this.send({ type: 'typing', is_typing: true });
    
    // Auto-stop typing after 3 seconds
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    this.typingTimeout = setTimeout(() => {
      this.stopTyping();
    }, 3000);
  }

  stopTyping() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
    this.send({ type: 'typing', is_typing: false });
  }

  addReaction(messageId, emoji) {
    this.send({
      type: 'reaction',
      message_id: messageId,
      emoji
    });
  }

  editMessage(messageId, newText) {
    this.send({
      type: 'edit',
      message_id: messageId,
      text: newText
    });
  }

  deleteMessage(messageId) {
    this.send({
      type: 'delete',
      message_id: messageId
    });
  }

  handleMessage(data) {
    switch (data.type) {
      case 'new_message':
        this.emit('message', data.message);
        break;
      case 'user_list':
        this.emit('userList', { users: data.users, count: data.count });
        break;
      case 'typing':
        this.emit('typing', { 
          typingUsers: data.typing_users,
          typingUserIds: data.typing_user_ids 
        });
        break;
      case 'reaction_update':
        this.emit('reaction', { 
          messageId: data.message_id, 
          reactions: data.reactions 
        });
        break;
      case 'message_edited':
        this.emit('messageEdited', { 
          messageId: data.message_id, 
          newText: data.new_text 
        });
        break;
      case 'message_deleted':
        this.emit('messageDeleted', { messageId: data.message_id, channel: data.channel });
        break;
      case 'mention':
        this.emit('mention', data);
        break;
      case 'error':
        this.emit('chatError', { message: data.message });
        break;
      case 'user_muted':
        this.emit('userMuted', { userId: data.user_id, duration: data.duration_minutes });
        break;
      case 'user_unmuted':
        this.emit('userUnmuted', { userId: data.user_id });
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  }

  // Admin functions
  muteUser(targetUserId, durationMinutes = 60) {
    this.send({
      type: 'admin_mute',
      target_user_id: targetUserId,
      duration_minutes: durationMinutes
    });
  }

  unmuteUser(targetUserId) {
    this.send({
      type: 'admin_unmute',
      target_user_id: targetUserId
    });
  }

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }
}

// Singleton instance
const chatWebSocket = new ChatWebSocket();

export default chatWebSocket;

// REST API functions for chat
export const chatApi = {
  getChannelMessages: async (channel) => {
    const response = await fetch(`${API_URL}/api/community/messages/${encodeURIComponent(channel)}`);
    if (!response.ok) throw new Error('Failed to get messages');
    return response.json();
  },

  // Alias for backwards compatibility
  getMessages: async (channel) => {
    const response = await fetch(`${API_URL}/api/community/messages/${encodeURIComponent(channel)}`);
    if (!response.ok) throw new Error('Failed to get messages');
    return response.json();
  },

  getOnlineUsers: async () => {
    const response = await fetch(`${API_URL}/api/community/presence/online`);
    if (!response.ok) throw new Error('Failed to get online users');
    return response.json();
  },

  searchMessages: async (query, channel = null) => {
    const params = new URLSearchParams({ q: query });
    if (channel) params.append('channel', channel);
    
    const response = await fetch(`${API_URL}/api/chat/messages/search?${params}`);
    if (!response.ok) throw new Error('Failed to search messages');
    return response.json();
  },

  getUnreadCounts: async (userId) => {
    const response = await fetch(`${API_URL}/api/chat/unread/${userId}`);
    if (!response.ok) throw new Error('Failed to get unread counts');
    return response.json();
  },

  markChannelRead: async (channel, userId) => {
    const response = await fetch(`${API_URL}/api/chat/mark-read/${channel}?user_id=${userId}`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to mark channel as read');
    return response.json();
  }
};

// Shared audio context for notification sounds
let sharedAudioContext = null;

// Initialize audio context (call this on user interaction to unlock)
export const initAudioContext = () => {
  if (!sharedAudioContext) {
    sharedAudioContext = new (window.AudioContext || window.webkitAudioContext)();
    console.log('Audio context initialized');
  }
  // Resume if suspended
  if (sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume().then(() => {
      console.log('Audio context resumed');
    });
  }
  return sharedAudioContext;
};

// Notification sound utility
export const playNotificationSound = () => {
  try {
    console.log('Attempting to play notification sound...');
    
    // Get or create audio context
    const audioContext = initAudioContext();
    
    // If still suspended, try to resume
    if (audioContext.state === 'suspended') {
      console.log('Audio context still suspended, cannot play sound');
      audioContext.resume();
      return;
    }
    
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Pleasant notification tone
    oscillator.frequency.value = 880; // A5 note
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15);
    console.log('Notification sound played successfully');
  } catch (e) {
    console.log('Could not play notification sound:', e);
  }
};
